import React, {useState} from 'react';

import {
  DialogContent,
  DialogTitle,
  SvgIcon,
  Typography,
} from '@mui/material';
import {
  Divider,
  Stack,
} from '@zoom/react-ui';
import {IconAlertCircleFillYes} from '@zoom/sun-ui-icon';
import {useTranslation} from 'react-i18next';
import {useNavigate} from 'react-router-dom';

import {accountAccessTokenInvalid} from '.';

import {
  ActionsArea,
  RowEmailAdress,
  RowNameAndService,
  ResolveAccountRow,
} from './Style';

import {ReactComponent as GoogleIcon} from 'assets/svg/google-icon.svg';
import {ReactComponent as ICloudIcon} from 'assets/svg/icloud.svg';
import {ReactComponent as MSIcon} from 'assets/svg/ms-icon.svg';
import {ReactComponent as ZoomIcon} from 'assets/svg/zoom-icon.svg';
import ConnectICloudDialog from 'Components/Integration/ConnectAccountModal/iCloud';
import {ZmPrimaryBtn, ZmQuaternaryBtn, ZmSecondaryBtn, ZmTertiaryBtn} from 'Components/ZmButtons';
import {ZmDialog} from 'Components/ZmDialogs';
import {getAccountKey} from 'Store/IntegrationStore';
import {parseName} from 'Utils';
import {PROVIDER_TYPE} from 'Utils/consts';
import {inZoomClient} from 'Utils/integration';
import {openUrlInBrowser, WEB_SCHEDULER_URL} from 'Utils/ZoomClientUtils';
import zoomPalette from 'zoomPalette';

const {APPLE, GOOGLE_CALENDAR, OUTLOOK_CALENDAR, ZOOM} = PROVIDER_TYPE;

export const iconMap = {
  [GOOGLE_CALENDAR]: GoogleIcon,
  [ZOOM]: ZoomIcon,
  [OUTLOOK_CALENDAR]: MSIcon,
  [APPLE]: ICloudIcon,
};
export const serviceNameMap = {
  [GOOGLE_CALENDAR]: 'Google',
  [ZOOM]: 'Zoom',
  [OUTLOOK_CALENDAR]: 'Office 365',
  [APPLE]: 'iCloud',
};

/**
 * Account Card Component
 * @param {Object} props
 * @param {Account} props.data
 * @param {?boolean} props.hideActions
 * @param {?function} props.onDisconnect
 * @param {?function} props.onReAuthorize
 * @return {ReactElement}
 */
function ResolveConnectionItem({data, hideActions, onDisconnect, onReAuthorize}) {
  const {t} = useTranslation();
  const [reauthAppleEmail, setReauthAppleEmail] = useState('');
  const name = parseName(data.email);

  return (
    <ResolveAccountRow>
      <ConnectICloudDialog
        iCloudEmail={reauthAppleEmail}
        onClose={() => setReauthAppleEmail('')}
        open={!!reauthAppleEmail}
      />
      <IconAlertCircleFillYes sx={{mr: '10px'}} />
      <SvgIcon
        sx={{width: '48px', height: '48px', marginRight: '16px', paddingTop: '2px'}}
        component={iconMap[data.service]}
        inheritViewBox
      />
      <div>
        <RowNameAndService component="h4">
          {name[0] + ' ' + (name?.[1] || '')} ({serviceNameMap[data.service]})
        </RowNameAndService>
        <RowEmailAdress>{data.email}</RowEmailAdress>
      </div>
      {!hideActions &&
        <ActionsArea>
          {data.service !== ZOOM && !!data.blocked && accountAccessTokenInvalid(data) &&
            <>
              <ZmTertiaryBtn
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  if (data.service === APPLE) {
                    setReauthAppleEmail(data.email);
                  } else {
                    onReAuthorize(data.service);
                  }
                }}
              >
                {t('integration.reAuthorize')}
              </ZmTertiaryBtn>

              <ZmQuaternaryBtn
                size="small"
                destructive
                onClick={(e) => {
                  e.stopPropagation();
                  onDisconnect(data);
                }}>
                {t('integration.disconnect')}
              </ZmQuaternaryBtn>
            </>
          }
          {data.service === ZOOM && !!data.blocked &&
            <>
              <ZmQuaternaryBtn
                size="small"
                destructive
                onClick={(e) => {
                  e.stopPropagation();
                  onDisconnect(data);
                }}>
                {t('integration.disconnect')}
              </ZmQuaternaryBtn>
            </>
          }
        </ActionsArea>
      }
    </ResolveAccountRow>
  );
}


/**
 * Account Settings Component
 * @param {*} props
 * @return {ReactElement}
 */
export function ConnectionErrorNotificationDialog(props) {
  const {accountList} = props;
  const {t} = useTranslation();
  const invalidAuthAccounts = accountList.filter(accountAccessTokenInvalid);
  const navigate = useNavigate();
  const [dismissed, setDismissed] = useState(false);

  return (
    <ZmDialog open={invalidAuthAccounts?.length && !dismissed} sx={{
      '& .MuiDialog-container': {
        '& .MuiPaper-root': {
          // minWidth: '600px',
          // minHeight: '300px',
        },
        'padding': '20px',
      },
    }}>
      <DialogTitle sx={{fontWeight: 600}}>
        {t('integration.invalidAccountAuthTitle')}
      </DialogTitle>
      <DialogContent>
        <Typography sx={{fontSize: '.875rem', mb: 2}}>
          {t('integration.invalidAccountAuthNotificationDesc')}
        </Typography>

        {/* <Stack
          sx={{
            border: '1px solid',
            borderRadius: '10px',
            borderColor: zoomPalette.gray[30],
            padding: 1,
            mb: 2,
          }}
          divider={<Divider sx={{margin: '12px 0px'}} />}
        >
          {invalidAuthAccounts.map((item) =>
            <ResolveConnectionItem
              hideActions
              key={getAccountKey(item)}
              data={item}
              onDisconnect={onDisconnect}
              onReAuthorize={onReAuthorize}
            />
          )}
        </Stack> */}

        <Stack sx={{flexDirection: 'row', justifyContent: 'flex-end', gap: '8px', mb: 2}}>
          <ZmSecondaryBtn
            onClick={() => setDismissed(true)}
            size="small"
          >
            {t('integration.invalidAccountAuthNotificationDismiss')}
          </ZmSecondaryBtn>
          <ZmPrimaryBtn
            onClick={() => {
              if (inZoomClient) {
                openUrlInBrowser(`${WEB_SCHEDULER_URL}?action=settings-account`);
              } else {
                navigate('/me/settings/account');
              }
            }}
            size="small"
          >
            {t('integration.invalidAccountAuthNotificationAction')}
          </ZmPrimaryBtn>
        </Stack>

      </DialogContent>
    </ZmDialog>
  );
}

/**
 * Account Settings Component
 * @param {*} props
 * @param {Account[]} props.accountList
 * @return {ReactElement}
 */
export function ConnectionErrorResolutionDialog(props) {
  const {accountList, onDisconnect, onReAuthorize} = props;
  const {t} = useTranslation();

  const invalidAuthAccounts = accountList.filter(accountAccessTokenInvalid);

  return (
    <ZmDialog open={invalidAuthAccounts?.length} sx={{
      '& .MuiDialog-container': {
        '& .MuiPaper-root': {
          minWidth: '600px',
        },
        'padding': '20px',
      },
    }}>
      <DialogTitle sx={{fontWeight: 500, fontSize: '1rem'}}>
        {t('integration.resolveAccountAuthTitle')}
      </DialogTitle>
      <DialogContent>
        <Stack
          sx={{
            border: '1px solid',
            borderRadius: '10px',
            borderColor: zoomPalette.gray[30],
            padding: 1,
            mb: 2,
          }}
          divider={<Divider sx={{margin: '12px 0px'}} />}
        >
          {invalidAuthAccounts.map((item) =>
            <ResolveConnectionItem
              key={getAccountKey(item)}
              data={item}
              onDisconnect={onDisconnect}
              onReAuthorize={onReAuthorize}
            />
          )}
        </Stack>
      </DialogContent>
    </ZmDialog>
  );
}
